import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translateText } from '../services/translationService';

export default function Help() {
  const [activeCategory, setActiveCategory] = useState('shipping');
  const [openItems, setOpenItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const { language } = useLanguage();
  const [translatedContent, setTranslatedContent] = useState({});
  const [translating, setTranslating] = useState(false);

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Fonctions pour gérer les actions de contact
  const handleEmailContact = () => {
    window.location.href = 'mailto:d4unaturally@gmail.com?subject=Question%20D4U%20Naturally&body=Hello,%20I%20am%20contacting%20you%20regarding...';
  };

  const handlePhoneContact = () => {
    window.location.href = 'tel:+41783292404';
  };

  const handleChatContact = () => {
    const message = language === 'fr' ? 'Ouverture du chat en direct... Notre service client vous répondra dans quelques instants.' :
                   language === 'ar' ? 'جاري فتح الدردشة المباشرة... سيتم الرد عليك في غضون دقائق.' :
                   'Opening live chat... Our customer service will respond in a few moments.';
    alert(message);
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      console.log('Search:', searchQuery);
    }
  };

  // Contenu original en anglais
  const originalContent = {
    hero: {
      title: "Help Center",
      subtitle: "We are here to help you. Find answers to your questions or contact our team.",
      searchPlaceholder: "Search for a question..."
    },
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about our services",
      categories: {
        shipping: "Shipping",
        returns: "Returns",
        products: "Products",
        account: "Account"
      }
    },
    contact: {
      title: "Contact Us",
      subtitle: "Multiple ways to get in touch",
      methods: {
        email: {
          title: "Email",
          description: "Response within 24h",
          details: "d4unaturally@gmail.com",
          button: "Contact"
        },
        phone: {
          title: "Phone",
          description: "Monday to Friday",
          details: "+41 78 329 24 04",
          button: "Call"
        },
        chat: {
          title: "Live Chat",
          description: "Immediate assistance",
          details: "9am-6pm weekdays",
          button: "Chat"
        }
      }
    },
    guides: {
      title: "Usage Guide",
      description: "Discover how to best use our products for optimal results.",
      link: "View Guides"
    },
    tracking: {
      title: "Order Status",
      description: "Track your order in real-time and receive notifications.",
      link: "Track My Order"
    }
  };

  // FAQs originales en anglais
  const originalFaqs = {
    shipping: [
      {
        question: "What are the delivery times?",
        answer: "Our delivery times are 2 to 5 working days in mainland France, and 5 to 10 days for international orders."
      },
      {
        question: "Do you deliver internationally?",
        answer: "Yes, we deliver throughout Europe. Fees and delivery times vary depending on the destination country."
      },
      {
        question: "Can I change my delivery address?",
        answer: "You can change your delivery address before your order is shipped from your account."
      }
    ],
    returns: [
      {
        question: "What is your return policy?",
        answer: "You have 30 days to return an unopened and unused product."
      },
      {
        question: "How to make a return?",
        answer: "Log into your account, go to 'My Orders' and follow the return process."
      },
      {
        question: "Are returns free?",
        answer: "Yes, we offer free returns for all orders in mainland France."
      }
    ],
    products: [
      {
        question: "Are your products certified organic?",
        answer: "Yes, all our products are certified organic by Ecocert and meet strict quality standards."
      },
      {
        question: "Where are your products made?",
        answer: "Our products are made in Morocco respecting traditions and international standards."
      },
      {
        question: "Do you test on animals?",
        answer: "No, all our products are cruelty-free and we never test on animals."
      }
    ],
    account: [
      {
        question: "How to create an account?",
        answer: "Click on 'My Account' at the top right and follow the registration process."
      },
      {
        question: "I forgot my password",
        answer: "Use the 'Forgot Password' function on the login page to reset it."
      },
      {
        question: "How to delete my account?",
        answer: "Contact our customer service who will take care of deleting your account and data."
      }
    ]
  };

  // Traduction du contenu
  useEffect(() => {
    const translateContent = async () => {
      if (language === 'en') {
        setTranslatedContent({
          ...originalContent,
          faqs: originalFaqs
        });
        return;
      }

      try {
        setTranslating(true);
        console.log(`🌐 Translating Help: en → ${language}`);

        // Traduire le contenu principal
        const translated = { ...originalContent };
        const translatedFaqs = { ...originalFaqs };

        // Traduire le contenu texte
        for (const [key, value] of Object.entries(originalContent)) {
          if (typeof value === 'string') {
            translated[key] = await translateText(value, language, 'en');
          } else if (typeof value === 'object') {
            for (const [subKey, subValue] of Object.entries(value)) {
              if (typeof subValue === 'string') {
                translated[key][subKey] = await translateText(subValue, language, 'en');
              } else if (typeof subValue === 'object') {
                for (const [methodKey, methodValue] of Object.entries(subValue)) {
                  if (typeof methodValue === 'string') {
                    translated[key][subKey][methodKey] = await translateText(methodValue, language, 'en');
                  }
                }
              }
            }
          }
        }

        // Traduire les FAQs
        for (const [category, items] of Object.entries(originalFaqs)) {
          translatedFaqs[category] = await Promise.all(
            items.map(async (item) => ({
              question: await translateText(item.question, language, 'en'),
              answer: await translateText(item.answer, language, 'en')
            }))
          );
        }

        setTranslatedContent({
          ...translated,
          faqs: translatedFaqs
        });

        console.log('✅ Help translated successfully');
      } catch (error) {
        console.error('💥 Translation error Help:', error);
        setTranslatedContent({
          ...originalContent,
          faqs: originalFaqs
        });
      } finally {
        setTranslating(false);
      }
    };

    translateContent();
  }, [language]);

  // Utiliser le contenu traduit ou original
  const content = translatedContent.hero ? translatedContent : originalContent;
  const faqCategories = content.faqs || originalFaqs;

  const contactMethods = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: content.contact?.methods?.email?.title || "Email",
      description: content.contact?.methods?.email?.description || "Response within 24h",
      details: "d4unaturally@gmail.com",
      button: content.contact?.methods?.email?.button || "Contact",
      action: handleEmailContact
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: content.contact?.methods?.phone?.title || "Phone",
      description: content.contact?.methods?.phone?.description || "Monday to Friday",
      details: "+41 78 329 24 04",
      button: content.contact?.methods?.phone?.button || "Call",
      action: handlePhoneContact
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      title: content.contact?.methods?.chat?.title || "Live Chat",
      description: content.contact?.methods?.chat?.description || "Immediate assistance",
      details: content.contact?.methods?.chat?.details || "9am-6pm weekdays",
      button: content.contact?.methods?.chat?.button || "Chat",
      action: handleChatContact
    }
  ];

  // Texte de recherche selon la langue
  const getSearchPlaceholder = () => {
    if (language === 'fr') return "Rechercher une question...";
    if (language === 'ar') return "ابحث عن سؤال...";
    return "Search for a question...";
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Overlay de traduction */}
      {translating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#d0b125]"></div>
            <p className="text-[#2f2a1e] font-light">
              {language === 'fr' ? 'Mise à jour des traductions...' : 
               language === 'ar' ? 'جاري تحديث الترجمات...' : 
               'Updating translations...'}
            </p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-light mb-6">{content.hero?.title || "Help Center"}</h1>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            {content.hero?.subtitle || "We are here to help you. Find answers to your questions or contact our team."}
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearch}
                placeholder={getSearchPlaceholder()}
                className="w-full px-6 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <button 
                onClick={handleSearch}
                className="absolute right-3 top-3 text-gray-400 hover:text-[#d0b125] transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">
              {content.faq?.title || "Frequently Asked Questions"}
            </h2>
            <p className="text-gray-600">
              {content.faq?.subtitle || "Everything you need to know about our services"}
            </p>
          </div>
          
          {/* Categories */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {Object.keys(faqCategories).map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full border transition-all duration-200 ${
                  activeCategory === category
                    ? 'bg-[#d0b125] text-white border-[#d0b125]'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-[#d0b125]'
                }`}
              >
                {content.faq?.categories?.[category] || 
                 (category === 'shipping' && 'Shipping') ||
                 (category === 'returns' && 'Returns') ||
                 (category === 'products' && 'Products') ||
                 (category === 'account' && 'Account')}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqCategories[activeCategory].map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{item.question}</span>
                  <svg 
                    className={`w-5 h-5 text-gray-400 transform transition-transform ${openItems[index] ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openItems[index] && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-600">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-4">
              {content.contact?.title || "Contact Us"}
            </h2>
            <p className="text-gray-600">
              {content.contact?.subtitle || "Multiple ways to get in touch"}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactMethods.map((method, index) => (
              <div key={index} className="bg-white rounded-lg p-6 text-center border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-600">
                  {method.icon}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{method.title}</h3>
                <p className="text-gray-600 mb-3">{method.description}</p>
                <p className="text-gray-900 font-medium">{method.details}</p>
                <button 
                  onClick={method.action}
                  className="mt-4 w-full bg-gray-900 text-white py-2 rounded-lg hover:bg-[#d0b125] transition-colors"
                >
                  {method.button}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Help */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-light text-gray-900 mb-4">
                {content.guides?.title || "Usage Guide"}
              </h3>
              <p className="text-gray-600 mb-4">
                {content.guides?.description || "Discover how to best use our products for optimal results."}
              </p>
              <Link to="/mag" className="text-[#d0b125] hover:text-amber-700 font-medium inline-flex items-center">
                {content.guides?.link || "View Guides"}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-light text-gray-900 mb-4">
                {content.tracking?.title || "Order Status"}
              </h3>
              <p className="text-gray-600 mb-4">
                {content.tracking?.description || "Track your order in real-time and receive notifications."}
              </p>
              <Link to="/track-order" className="text-[#d0b125] hover:text-amber-700 font-medium inline-flex items-center">
                {content.tracking?.link || "Track My Order"}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 left-4 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded z-50">
          Language: {language}
        </div>
      )}
    </div>
  );
}